import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Home, Plus, ListOrdered, User, Package2, Truck, Wallet, BarChart3, Users, ShieldCheck, Settings, LogOut, Sparkles, Bell, TrendingUp, Banknote, Globe2, AlertTriangle, MoreHorizontal, X, Repeat2, FileSearch, Send } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useIsKeyboardOpen } from "@/lib/useIsKeyboardOpen";
import Logo from "./Logo";
import { ROLE_LABEL } from "@/lib/format";
import CreditReferralToast from "./CreditReferralToast";

const CUSTOMER_TABS = [
  { to: "/app", label: "Home", icon: Home, end: true, testId: "nav-customer-home" },
  { to: "/app/new", label: "Nuovo", icon: Plus, testId: "nav-customer-new" },
  { to: "/app/orders", label: "Ordini", icon: ListOrdered, testId: "nav-customer-orders" },
  { to: "/app/profile", label: "Profilo", icon: User, testId: "nav-customer-profile" },
];

const DRIVER_TABS = [
  { to: "/driver", label: "Disponibili", icon: Package2, end: true, testId: "nav-driver-home" },
  { to: "/driver/active", label: "Attivi", icon: Truck, testId: "nav-driver-active" },
  { to: "/driver/earnings", label: "Guadagni", icon: Wallet, testId: "nav-driver-earnings" },
  { to: "/driver/profile", label: "Profilo", icon: User, testId: "nav-driver-profile" },
];

const ADMIN_TABS = [
  { to: "/admin", label: "Dashboard", icon: BarChart3, end: true, testId: "nav-admin-home" },
  { to: "/admin/ops", label: "Centro Operativo", icon: AlertTriangle, testId: "nav-admin-ops" },
  { to: "/admin/orders", label: "Ordini", icon: ListOrdered, testId: "nav-admin-orders" },
  { to: "/admin/users",                label: "Utenti",   icon: Users, testId: "nav-admin-users" },
  { to: "/admin/users?role=customers", label: "Clienti",  icon: Users, testId: "nav-admin-clients" },
  { to: "/admin/drivers", label: "Drivers", icon: ShieldCheck, testId: "nav-admin-drivers" },
  { to: "/admin/driver-pilot", label: "Pilot driver", icon: Send, testId: "nav-admin-driver-pilot" },
  { to: "/admin/leads", label: "Lead B2B", icon: Sparkles, testId: "nav-admin-leads" },
  { to: "/admin/acquisition", label: "Acquisizione", icon: TrendingUp, testId: "nav-admin-acquisition" },
  { to: "/admin/payouts", label: "Payouts", icon: Banknote, testId: "nav-admin-payouts" },
  { to: "/admin/refunds", label: "Rimborsi", icon: Banknote, testId: "nav-admin-refunds" },
  { to: "/admin/notifications", label: "Notifiche", icon: Bell, testId: "nav-admin-notifications" },
  { to: "/admin/retry-queue", label: "Retry queue", icon: Repeat2, testId: "nav-admin-retry-queue" },
  { to: "/admin/audit-log", label: "Audit log", icon: FileSearch, testId: "nav-admin-audit-log" },
  { to: "/admin/cross-border", label: "Cross-border", icon: Globe2, testId: "nav-admin-cross-border" },
  { to: "/admin/settings", label: "Settings", icon: Settings, testId: "nav-admin-settings" },
];

// Phase 43.45 — Mobile bottom nav admin: solo 4 voci principali + "Altro"
// (l'admin desktop usa la sidebar laterale, mostra tutte le 12 voci)
const ADMIN_MOBILE_PRIMARY = ["nav-admin-home", "nav-admin-ops", "nav-admin-orders", "nav-admin-drivers"];

function pickTabs(role) {
  if (role === "driver") return DRIVER_TABS;
  if (role === "admin") return ADMIN_TABS;
  return CUSTOMER_TABS;
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const tabs = pickTabs(user?.role);
  const isAdmin = user?.role === "admin";
  // Phase 43.43 — nascondi bottom nav quando la tastiera iOS è aperta
  const keyboardOpen = useIsKeyboardOpen();
  // Phase 43.45 — Sheet "Altro" per admin mobile
  const [adminMoreOpen, setAdminMoreOpen] = useState(false);

  // Per admin: mobile mostra solo 4 voci primarie + "Altro"; desktop usa sidebar.
  const primaryTabs = isAdmin
    ? tabs.filter((t) => ADMIN_MOBILE_PRIMARY.includes(t.testId))
    : tabs;
  const secondaryTabs = isAdmin
    ? tabs.filter((t) => !ADMIN_MOBILE_PRIMARY.includes(t.testId))
    : [];

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  // Phase 43.16 — admin ha bisogno di più larghezza per tabelle Ordini/Drivers/etc.
  // (sidebar 220px + content fluido). Sopra max-w-7xl per evitare tabelle compresse.
  const shellMaxWidth = isAdmin ? "max-w-screen-2xl" : "max-w-6xl";

  return (
    <div className="bg-dapnow-bg" style={{ minHeight: "100dvh" }} data-testid="app-shell-root">
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-black/5 glass-nav">
        <div className={`mx-auto ${shellMaxWidth} px-4 sm:px-6 h-20 flex items-center justify-between gap-3`}>
          <Logo size="md" />
          <div className="flex min-w-0 items-center gap-3">
            <div className="hidden sm:flex min-w-0 max-w-[18rem] flex-col items-end">
              <span className="max-w-full truncate text-sm font-semibold tracking-tight">{user?.name}</span>
              <span className="overline text-[10px]">{ROLE_LABEL[user?.role] || user?.role}</span>
            </div>
            <button
              onClick={handleLogout}
              data-testid="header-logout-btn"
              className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-dapnow-line bg-white px-3 h-10 text-sm font-medium hover:bg-dapnow-bg transition"
            >
              <LogOut className="h-4 w-4" /> Esci
            </button>
          </div>
        </div>
      </header>

      {/* Desktop side rail for admin — Phase 43.49: padding orizzontale rispetta
          env(safe-area-inset-left/right) per centratura simmetrica su iPhone con
          Dynamic Island (in portrait alcuni iOS riportano inset destro ~12px). */}
      <div
        className={`mx-auto ${shellMaxWidth} py-6 pb-[calc(7rem+env(safe-area-inset-bottom))] md:pb-10`}
        style={{
          paddingLeft: "max(1rem, env(safe-area-inset-left))",
          paddingRight: "max(1rem, env(safe-area-inset-right))",
        }}
      >
        {isAdmin ? (
          <div className="grid md:grid-cols-[220px_minmax(0,1fr)] gap-6">
            <aside className="hidden md:block">
              <nav className="sticky top-24 space-y-1">
                {tabs.map((t) => (
                  <NavLink
                    key={t.to}
                    to={t.to}
                    end={t.end}
                    data-testid={t.testId}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition ${
                        isActive ? "bg-dapnow-blue text-white shadow-sm" : "text-dapnow-ink hover:bg-white"
                      }`
                    }
                  >
                    <t.icon className="h-4 w-4" /> {t.label}
                  </NavLink>
                ))}
              </nav>
            </aside>
            <main className="min-w-0 animate-fade-up">{children}</main>
          </div>
        ) : (
          <main className="min-w-0 animate-fade-up">{children}</main>
        )}
      </div>

      {/* Bottom nav — Phase 43.43 iOS keyboard-aware + Phase 44.15 fix gap bianco.
          Root cause precedente: willChange/contain/backface creavano un composite
          layer GPU che NON tracciava il visual viewport iOS; quando l'address bar
          Safari si nascondeva/mostrava, il layer rimaneva staccato lasciando un
          gap bianco visibile sotto la nav. Sostituiti con opacity+pointer-events
          (no GPU layer dedicato) → la nav resta solidale con bottom: 0. */}
      <nav
        className={`fixed bottom-0 inset-x-0 z-40 border-t border-black/10 bg-white transition-opacity duration-150 ${
          user?.role === "admin" ? "md:hidden" : ""
        } ${keyboardOpen ? "opacity-0 pointer-events-none" : "opacity-100"}`}
        data-testid="bottom-nav"
        aria-hidden={keyboardOpen ? "true" : "false"}
        style={{
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}
      >
        <div className={`mx-auto ${shellMaxWidth} px-2 sm:px-6`}>
          <ul
            className="grid"
            style={{
              gridTemplateColumns: `repeat(${primaryTabs.length + (isAdmin ? 1 : 0)}, minmax(0, 1fr))`,
            }}
          >
            {primaryTabs.map((t) => (
              <li key={t.to}>
                <NavLink
                  to={t.to}
                  end={t.end}
                  data-testid={t.testId}
                  className={({ isActive }) =>
                    `flex flex-col items-center justify-start pt-2 pb-2.5 gap-1 min-h-[60px] text-[11px] font-semibold tracking-tight leading-none ${
                      isActive ? "text-dapnow-blue" : "text-dapnow-ink/70"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition ${
                          isActive ? "bg-dapnow-blue-soft" : ""
                        }`}
                      >
                        <t.icon className="h-5 w-5" strokeWidth={2.25} />
                      </span>
                      <span className="leading-none truncate w-full text-center">{t.label}</span>
                    </>
                  )}
                </NavLink>
              </li>
            ))}
            {isAdmin && (
              <li>
                <button
                  type="button"
                  onClick={() => setAdminMoreOpen(true)}
                  data-testid="nav-admin-more"
                  className="flex w-full flex-col items-center justify-start pt-2 pb-2.5 gap-1 min-h-[60px] text-[11px] font-semibold tracking-tight leading-none text-dapnow-ink/70"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl">
                    <MoreHorizontal className="h-5 w-5" strokeWidth={2.25} />
                  </span>
                  <span className="leading-none truncate w-full text-center">Altro</span>
                </button>
              </li>
            )}
          </ul>
        </div>
      </nav>

      {/* Phase 43.45 — Admin "Altro" sheet con voci secondarie */}
      {isAdmin && adminMoreOpen && (
        <>
          <div
            className="fixed inset-0 z-[60] bg-black/50"
            onClick={() => setAdminMoreOpen(false)}
            data-testid="admin-more-backdrop"
          />
          <div
            className="fixed bottom-0 inset-x-0 z-[61] bg-white rounded-t-2xl shadow-2xl"
            style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
            data-testid="admin-more-sheet"
          >
            <div className="px-5 py-4 border-b border-dapnow-line flex items-center justify-between">
              <p className="font-display text-lg font-bold tracking-tight">Altre sezioni</p>
              <button
                onClick={() => setAdminMoreOpen(false)}
                className="rounded-lg border border-dapnow-line h-9 w-9 inline-flex items-center justify-center hover:bg-dapnow-bg"
                aria-label="Chiudi"
                data-testid="admin-more-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 p-4 max-h-[60vh] overflow-y-auto">
              {secondaryTabs.map((t) => (
                <NavLink
                  key={t.to}
                  to={t.to}
                  end={t.end}
                  onClick={() => setAdminMoreOpen(false)}
                  data-testid={`admin-more-${t.testId}`}
                  className="flex items-center gap-3 rounded-xl border border-dapnow-line px-3 py-3 text-sm font-semibold hover:bg-dapnow-bg"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-dapnow-blue-soft text-dapnow-blue">
                    <t.icon className="h-4 w-4" strokeWidth={2.25} />
                  </span>
                  <span className="truncate">{t.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        </>
      )}
      {/* Phase 44.07 — Premium credit toast (one-shot referral grant) */}
      <CreditReferralToast />
    </div>
  );
}
